'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download } from '@mui/icons-material';
import { Button, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useQueryCtmEvents, useUpdateCtmEvent } from '@/hooks/api/useCtms';
import { useNotification } from '@/contexts/NotificationContext';
import { formatDate } from '@/stack-shared/utils/gen-utils';

const CtmEventPage: React.FC = () => {
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const [startDate, setStartDate] = useState<Date | null>(lastMonth);
  const [endDate, setEndDate] = useState<Date | null>(now);

  const queryCtmEventsMutation = useQueryCtmEvents();
  const updateCtmEventMutation = useUpdateCtmEvent();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    {
      accessorKey: 'date', header: 'Date',
      Cell: ({ row }: any) => formatDate(row?.original?.date),
    },
    { accessorKey: 'time', header: 'Time' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'assetId', header: 'Asset ID' },
    { accessorKey: 'statusChange', header: 'Status Change' },
    { accessorKey: 'event', header: 'Event' },
    { accessorKey: 'damageRepair', header: 'Damage/Repair' },
    { accessorKey: 'provisioningCode', header: 'Provisioning Code' },
    { accessorKey: 'notes', header: 'Notes' },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        const rowData = row.original;
        const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
        const actionsOpen = Boolean(actionsAnchorEl);

        const handleMenuOpen = (event: any) => setActionsAnchorEl(event.currentTarget);
        const handleMenuClose = () => setActionsAnchorEl(null);

        return (
          <>
            <IconButton onClick={handleMenuOpen} color="primary" title="Options">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => {
                setSelectedEvent(rowData);
                setEditModalVisible(true);
              }}>
                <ListItemIcon><EditIcon /></ListItemIcon>
                <Typography>Edit</Typography>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'CTM Events' },
  ];

  const rightContent = (
    <>
      <Button variant="outlined" startIcon={<Download />} sx={{ textTransform: 'none' }}>
        Export
      </Button>
      <Button onClick={() => setCreateModalVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        + Add Event
      </Button>
    </>
  );

  const sharedEventFields = [{
    groupName: 'main',
    fields: [
      { label: 'Date', name: 'date', type: GENERIC_FORM_FIELD_TYPE.DATE, required: true, columns: 6 },
      { label: 'Time', name: 'time', type: GENERIC_FORM_FIELD_TYPE.TIME, required: false, columns: 6 },
      { label: 'Location', name: 'location', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Asset ID', name: 'assetId', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Event', name: 'event', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Damage/Repair', name: 'damageRepair', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Provisioning Code', name: 'provisioningCode', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Notes', name: 'notes', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
    ],
  }];

  const editEventFields = cloneDeep(sharedEventFields);
  const createEventFields = cloneDeep(sharedEventFields);

  const fetchData = async () => {
    if (!startDate || !endDate) return;

    queryCtmEventsMutation.mutate(
      {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search,
        orderBy: sorting[0]?.id || 'id',
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
      },
      {
        onSuccess: (result: any) => {
          setData(result.data || []);
          setTotalRows(result.total || 0);
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to fetch CTM events');
        },
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, search, tableRefreshKey, startDate, endDate]);

  return (
    <>
      <GenericFormModal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          console.log("values?", values);
          setCreateModalVisible(false);
          setTableRefreshKey(tableRefreshKey + 1);
        }}
        initialValues={{}}
        fieldGroups={createEventFields}
        title={'Create CTM Event'}
      />

      <GenericFormModal
        visible={editModalVisible}
        onDismiss={() => setEditModalVisible(false)}
        onSubmit={async (values: any) => {
          updateCtmEventMutation.mutate(values, {
            onSuccess: (data: any) => {
              console.log("Updated", data);
              setEditModalVisible(false);
              setTableRefreshKey(tableRefreshKey + 1);
              showSuccess("CTM event updated successfully");
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update CTM event');
            },
          });
        }}
        initialValues={selectedEvent}
        fieldGroups={editEventFields}
        title={'Edit CTM Event'}
      />

      <TitleBar breadcrumbs={breadcrumbs} title="CTM Events" rightContent={rightContent} />

      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
      />
    </>
  );
};

export default CtmEventPage;
